import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Stack, useTheme } from "@mui/material";
import type { CsrIndicatorForm } from "../PillarForm.utils";
import { FoodsTextInput, RichTextEditor, Textarea, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { getLeverValueColor, LeverValueColor, leverValueColors } from "src/utils/csrStrategyValueColor";
import { useId } from "react";

type IndicatorFieldsProps = {
    indicator: CsrIndicatorForm;
    setIndicator: (newIndicators: CsrIndicatorForm) => void;
};
export const IndicatorFields = (props: IndicatorFieldsProps) => {
    const { indicator, setIndicator } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const leverShortNameId = useId();
    const leverShortNameHelperId = `${leverShortNameId}-helper`;
    const leverShortNameCounterId = `${leverShortNameId}-counter`;
    const leverShortNameCount = indicator.leverShortName?.length ?? 0;
    const isLeverShortNameOverRecommendation = leverShortNameCount > 60;
    const valueColorLabels: Record<LeverValueColor, string> = {
        green: t("settings.csr.form.indicator.fields.value_color.options.green", "Green"),
        orange: t("settings.csr.form.indicator.fields.value_color.options.orange", "Orange"),
        red: t("settings.csr.form.indicator.fields.value_color.options.red", "Red"),
        grey: t("settings.csr.form.indicator.fields.value_color.options.grey", "Grey"),
    };
    return (
        <Stack padding={{ xs: 3, md: 4 }} gap={theme.spacing(3)} width="100%">
            <Stack gap={1}>
                <Typography component="label" htmlFor={leverShortNameId} variant="big-medium">
                    {t("settings.csr.form.indicator.fields.lever_short_name", "Lever short name") + "*"}
                </Typography>
                <Typography id={leverShortNameHelperId} variant="body-medium" color={theme.custom.grey[1400]}>
                    {t(
                        "settings.csr.form.indicator.fields.lever_short_name_helper",
                        "Displayed on the strategy card. Aim for 2 lines - around 60 characters. Longer text goes in the full name below.",
                    )}
                </Typography>
                <FoodsTextInput
                    value={indicator.leverShortName ?? ""}
                    ExtraInputProps={{
                        id: leverShortNameId,
                        required: true,
                        "aria-describedby": `${leverShortNameHelperId} ${leverShortNameCounterId}`,
                    }}
                    onChange={(newValue) => {
                        setIndicator({
                            ...indicator,
                            leverShortName: newValue,
                        });
                    }}
                />
                <Typography
                    id={leverShortNameCounterId}
                    variant="small-medium"
                    color={isLeverShortNameOverRecommendation ? theme.custom.orange[600] : theme.custom.grey[1400]}
                >
                    {t(
                        "settings.csr.form.indicator.fields.lever_short_name_counter",
                        "{{count}} characters (60 recommended max)",
                        { count: leverShortNameCount },
                    )}
                </Typography>
            </Stack>
            <Stack gap={1}>
                <Typography variant="big-medium">
                    {t("settings.csr.form.indicator.fields.lever_full_name", "Lever full name")}
                </Typography>
                <Textarea
                    value={indicator.leverFullName ?? ""}
                    onChange={(newValue) => {
                        setIndicator({
                            ...indicator,
                            leverFullName: newValue,
                        });
                    }}
                    minRows={3}
                />
            </Stack>
            <Stack gap={1}>
                <Typography variant="big-medium">
                    {t("settings.csr.form.indicator.fields.indicator_label", "Indicator label") + "*"}
                </Typography>
                <FoodsTextInput
                    value={indicator.indicatorLabel ?? ""}
                    onChange={(newValue) => {
                        setIndicator({
                            ...indicator,
                            indicatorLabel: newValue,
                        });
                    }}
                />
            </Stack>
            <Stack flexDirection={{ xs: "column", md: "row" }} gap={theme.spacing(3)}>
                <Stack flex={1} gap={1}>
                    <Typography variant="big-medium">{t("settings.csr.form.indicator.fields.unit")}</Typography>
                    <FoodsTextInput
                        value={indicator.unitCode}
                        onChange={(newValue) => {
                            setIndicator({
                                ...indicator,
                                unitCode: newValue,
                            });
                        }}
                    />
                </Stack>
                <Stack flex={1} gap={1}>
                    <Typography variant="big-medium">
                        {t("settings.csr.form.indicator.fields.currentvalue") + "*"}
                    </Typography>
                    <FoodsTextInput
                        value={indicator.currentValue}
                        onChange={(newValue) => {
                            setIndicator({
                                ...indicator,
                                currentValue: newValue,
                            });
                        }}
                    />
                </Stack>
            </Stack>
            <FormControl>
                <Typography component="legend" variant="big-medium">
                    {t("settings.csr.form.indicator.fields.value_color.label", "Value color") + "*"}
                </Typography>
                <RadioGroup
                    row
                    value={indicator.valueColor ?? ""}
                    onChange={(event) =>
                        setIndicator({ ...indicator, valueColor: event.target.value as LeverValueColor })
                    }
                    sx={{ columnGap: 2, rowGap: 0.5 }}
                >
                    {leverValueColors.map((value) => {
                        const color = getLeverValueColor(theme, value);
                        return (
                            <FormControlLabel
                                key={value}
                                value={value}
                                label={valueColorLabels[value]}
                                control={
                                    <Radio
                                        size="small"
                                        sx={{ color, "&.Mui-checked": { color } }}
                                        icon={
                                            <Box
                                                sx={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: "50%",
                                                    backgroundColor: color,
                                                    border: `2px solid ${theme.palette.common.white}`,
                                                    boxShadow: `0 0 0 1px ${theme.custom.grey[500]}`,
                                                }}
                                            />
                                        }
                                        checkedIcon={
                                            <Box
                                                sx={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: "50%",
                                                    backgroundColor: color,
                                                    border: `2px solid ${theme.palette.common.white}`,
                                                    boxShadow: `0 0 0 2px ${color}`,
                                                }}
                                            />
                                        }
                                    />
                                }
                            />
                        );
                    })}
                </RadioGroup>
            </FormControl>
            <Stack flexDirection={{ xs: "column", md: "row" }} gap={theme.spacing(3)}>
                <Stack flex={1} gap={1}>
                    <Typography variant="big-medium">
                        {t("settings.csr.form.indicator.fields.current_campaign_goal", "Current campaign Goal")}
                    </Typography>
                    <FoodsTextInput
                        value={indicator.shortTermTarget}
                        onChange={(newValue) => setIndicator({ ...indicator, shortTermTarget: newValue })}
                    />
                </Stack>
                <Stack flex={1} gap={1}>
                    <Typography variant="big-medium">
                        {t("settings.csr.form.indicator.fields.target_campaign_goal", "Target campaign Goal")}
                    </Typography>
                    <FoodsTextInput
                        value={indicator.longTermTarget}
                        onChange={(newValue) => setIndicator({ ...indicator, longTermTarget: newValue })}
                    />
                </Stack>
            </Stack>
            <Stack gap={theme.spacing(1)}>
                <Typography variant="big-medium">
                    {t(
                        "settings.csr.form.indicator.fields.rich_content",
                        "Detailed information about the lever (side drawer content)",
                    )}
                </Typography>
                <RichTextEditor
                    allowBase64Images
                    key={indicator["@id"] ?? indicator.rowIndex}
                    initialContent={indicator.richContent ?? ""}
                    onChange={(newValue) => {
                        setIndicator({
                            ...indicator,
                            richContent: newValue,
                        });
                    }}
                    placeholder={t(
                        "settings.csr.form.indicator.fields.rich_content.placeholder",
                        "Add optional explanatory content for the lever details panel",
                    )}
                    value={indicator.richContent ?? ""}
                />
            </Stack>
        </Stack>
    );
};
